/**
 * Admin page-specific cache helpers
 * @copyright 2025-2026 Soter Technologies, LLC
 * @file pages/admin.js
 * @author Bryan Lai
 */

import ApiManager from '../../ApiManager'

const TTL = 15 * 60 * 1000 // 15 minutes

const CACHE_KEYS = {
    USERS: 'pages.admin.users',
    SYSTEM_MESSAGES: 'pages.admin.systemmessages',
    DEVICES: 'pages.admin.devices',
    BLACKLIST: 'pages.admin.blacklist',
    EMAIL_LOG: 'pages.admin.emaillog',
    SUB_SITES: 'pages.admin.subsites',
    SMS_LOG: 'pages.admin.smslog',
    WEBHOOK_LOG: 'pages.admin.webhooklog',
    ALL_ALERTS: 'pages.admin.allAlerts',
}

class AdminCache {
    constructor(cacheManager) {
        this.cacheManager = cacheManager
    }

    /**
     * Private helper function that handles result when calling a specific count function.
     * @param {string} key - Cache key 
     * @param {Function} countFn - Router count method/function, 
     * @returns 
     */
    async _getOrCount(key, countFn) {
        const cache = this.cacheManager
        const cachedData = cache.get(key) // Cached data, null if it does not exist, else {data: <data>, expiry: <Date>}
        let result // Return result

        if (cachedData === null) {
            // Call count function
            const res = await countFn()

            // Build result from count function response.
            result = {
                isSuccess: res.isSuccess,
                fromCache: false,
                data: { ...(res.data ?? {}), LastUpdated: Date.now() }
            }

            // Response handling
            if (res.isSuccess) {
                // Only cache if successful
                cache.set(key, result.data, TTL)
            }
            else {
                // Build failed result
                result = {
                    isSuccess: false,
                    fromCache: false,
                    data: {LastUpdated: Date.now()}
                }
            }
        } 
        // Grab from cache if it exists
        else {
            // Build result from cache values 
            result = {
                isSuccess: true,
                fromCache: true,
                data: cachedData.data
            }
        }

        return result
    }


    /**
     * Gets count for all users
     * @returns {Promise<{isSuccess: boolean, data: {PageNumber: number, Results: Array<any>, ReturnedResults: number, TotalPages: number, TotalResults: number, LastUpdated: number}, fromCache?: boolean}>}
     */
    // eslint-disable-next-line require-await
    async getAllUserCount() {
        return this._getOrCount(CACHE_KEYS.USERS, () =>
            ApiManager.router.users.getAllUsers({ countOnly: true })
        )
    }

    /**
     * Gets count for system messages
     * @returns {Promise<{isSuccess: boolean, data: {PageNumber: number, Results: Array<any>, ReturnedResults: number, TotalPages: number, TotalResults: number, LastUpdated: number}, fromCache?: boolean}>}
     */
    // eslint-disable-next-line require-await
    async getAllSystemMessagesCount() {
        return this._getOrCount(CACHE_KEYS.SYSTEM_MESSAGES, () =>
            ApiManager.router.systemmessages.getAllSystemMessages({ countOnly: true })
        )
    }

    /**
     * Gets count for devices
     * @returns {Promise<{isSuccess: boolean, data: {PageNumber: number, Results: Array<any>, ReturnedResults: number, TotalPages: number, TotalResults: number, LastUpdated: number}, fromCache?: boolean}>}
     */
    // eslint-disable-next-line require-await
    async getAllDevicesCount() {
        return this._getOrCount(CACHE_KEYS.DEVICES, () =>
            ApiManager.router.devices.getAllDevices({ countOnly: true })
        )
    }

    /**
     * Gets count for blacklists filtered by date range
     * @param {string} startDate - Start date filter
     * @param {string} endDate - End date filter
     * @returns {Promise<{isSuccess: boolean, data: {PageNumber: number, Results: Array<any>, ReturnedResults: number, TotalPages: number, TotalResults: number, LastUpdated: number}, fromCache?: boolean}>}
     */
    // eslint-disable-next-line require-await
    async getBlackListCount(startDate, endDate) {
        return this._getOrCount(CACHE_KEYS.BLACKLIST, () =>
            ApiManager.router.blacklists.getBlacklists({
                countOnly: true,
                startDate,
                endDate,
                isBlocked: true,
            })
        )
    }

    /**
     * Gets count for email logs filtered by date range
     * @param {string} startDate - Start date filter
     * @param {string} endDate - End date filter
     * @returns {Promise<{isSuccess: boolean, data: {PageNumber: number, Results: Array<any>, ReturnedResults: number, TotalPages: number, TotalResults: number, LastUpdated: number}, fromCache?: boolean}>}
     */
    // eslint-disable-next-line require-await
    async getEmailLogCount(startDate, endDate) {
        return this._getOrCount(CACHE_KEYS.EMAIL_LOG, () =>
            ApiManager.router.emailLog.getEmailLog({
                countOnly: true,
                startDate,
                endDate,
            })
        )
    }

    /**
     * Gets count for subsites
     * @returns {Promise<{isSuccess: boolean, data: {PageNumber: number, Results: Array<any>, ReturnedResults: number, TotalPages: number, TotalResults: number, LastUpdated: number}, fromCache?: boolean}>}
     */
    // eslint-disable-next-line require-await
    async getAllSubSitesCount() {
        return this._getOrCount(CACHE_KEYS.SUB_SITES, () =>
            ApiManager.router.subsites.getAllSubSites({ countOnly: true })
        )
    }

    /**
     * Gets count for SMS logs filtered by date range
     * @param {string} startDate - Start date filter
     * @param {string} endDate - End date filter
     * @returns {Promise<{isSuccess: boolean, data: {PageNumber: number, Results: Array<any>, ReturnedResults: number, TotalPages: number, TotalResults: number, LastUpdated: number}, fromCache?: boolean}>}
     */
    // eslint-disable-next-line require-await
    async getSmsLogCount(startDate, endDate) {
        return this._getOrCount(CACHE_KEYS.SMS_LOG, () =>
            ApiManager.router.smsLog.getSmsLog({
                countOnly: true,
                startDate,
                endDate,
            })
        )
    }

    /**
     * Gets count for webhook logs filtered by date range
     * @param {string} startDate - Start date filter
     * @param {string} endDate - End date filter
     * @returns {Promise<{isSuccess: boolean, data: {PageNumber: number, Results: Array<any>, ReturnedResults: number, TotalPages: number, TotalResults: number, LastUpdated: number}, fromCache?: boolean}>}
     */
    // eslint-disable-next-line require-await
    async getWebhookLogCount(startDate, endDate) {
        return this._getOrCount(CACHE_KEYS.WEBHOOK_LOG, () =>
            ApiManager.router.webhookLog.getAdminWebhookLog({
                countOnly: true,
                startDate,
                endDate,
            })
        )
    }

    /**
     * Gets count for all alerts filtered by date range
     * @param {string} startDate - Start time filter
     * @param {string} endDate - End time filter
     * @returns {Promise<{isSuccess: boolean, data: {PageNumber: number, Results: Array<any>, ReturnedResults: number, TotalPages: number, TotalResults: number, LastUpdated: number}, fromCache?: boolean}>}
     */
    // eslint-disable-next-line require-await
    async getAllAlertsCount(startDate, endDate) {
        return this._getOrCount(CACHE_KEYS.ALL_ALERTS, () =>
            ApiManager.router.alerts.getAllAlerts({
                countOnly: true,
                startTime: startDate,
                endTime: endDate,
            })
        )
    }

    /**
     * Clears all admin-level cached keys in CACHE_KEYS from the cache manager
     */
    clear() {
        Object.values(CACHE_KEYS).forEach(key => {
            this.cacheManager.delete(key)
        })
    }
}

export default AdminCache
